import React, { useState } from 'react';
import { Chip } from '../../lib/types';
import { US_STATES } from '../../lib/utils';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import StepProgress from '../../components/StepProgress/StepProgress';
import Button from '../../components/Button/Button';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { PortalField } from '../../components/FormField/PortalField';
import { FormGrid } from '../../components/FormField/FormGrid';
import ConfirmSummary from '../../components/ConfirmSummary/ConfirmSummary';
import styles from './Chips.module.scss';

interface TransferChipModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  onTransfer: (chipId: string, adopter: AdopterFormData) => void;
}

export interface AdopterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  apt: string;
  city: string;
  state: string;
  zip: string;
}

export const INITIAL_ADOPTER: AdopterFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  apt: '',
  city: '',
  state: '',
  zip: '',
};

export default function TransferChipModal({ open, onClose, chip, onTransfer }: TransferChipModalProps) {
  const { showToast } = useToast();
  const [step, setStep] = useState(1);
  const [adopter, setAdopter] = useState<AdopterFormData>({ ...INITIAL_ADOPTER });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  function reset() {
    setStep(1);
    setAdopter({ ...INITIAL_ADOPTER });
    setErrors({});
  }

  function handleClose() {
    reset();
    onClose();
  }

  function updateAdopter<K extends keyof AdopterFormData>(key: K, value: string) {
    setAdopter((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function validateAdopter(): boolean {
    const errs: Record<string, boolean> = {};
    if (!adopter.firstName.trim()) errs.firstName = true;
    if (!adopter.lastName.trim()) errs.lastName = true;
    if (!adopter.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adopter.email)) errs.email = true;
    if (!adopter.phone.trim()) errs.phone = true;
    if (!adopter.street.trim()) errs.street = true;
    if (!adopter.city.trim()) errs.city = true;
    if (!adopter.state) errs.state = true;
    if (!adopter.zip.trim() || !/^\d{5}$/.test(adopter.zip)) errs.zip = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (!validateAdopter()) return;
    setStep(2);
  }

  function handleConfirm() {
    if (!chip) return;
    onTransfer(chip.id, adopter);
    showToast(`Owner added to ${chip.id}`);
    handleClose();
  }

  if (!chip) return null;

  const fullAddress = [
    adopter.street,
    adopter.apt ? `Apt ${adopter.apt}` : '',
    `${adopter.city}, ${adopter.state} ${adopter.zip}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <StepProgress currentStep={step} totalSteps={2} />

      {/* Step 1: Adopter Info */}
      {step === 1 && (
        <div>
          <ModalTitle>Add owner</ModalTitle>
          <p className={styles.modalDescription}>Enter the owner&apos;s contact information to assign this chip.</p>
          <FormGrid>
            <PortalField label="First Name *" error="Required" showError={errors.firstName}>
              <PortalInput
                value={adopter.firstName}
                onChange={(e) => updateAdopter('firstName', e.target.value)}
                placeholder="First name"
                hasError={errors.firstName}
              />
            </PortalField>

            <PortalField label="Last Name *" error="Required" showError={errors.lastName}>
              <PortalInput
                value={adopter.lastName}
                onChange={(e) => updateAdopter('lastName', e.target.value)}
                placeholder="Last name"
                hasError={errors.lastName}
              />
            </PortalField>

            <PortalField label="Email *" error="Valid email required" showError={errors.email}>
              <PortalInput
                value={adopter.email}
                onChange={(e) => updateAdopter('email', e.target.value)}
                placeholder="Email address"
                type="email"
                hasError={errors.email}
              />
            </PortalField>

            <PortalField label="Phone *" error="Required" showError={errors.phone}>
              <PortalInput
                value={adopter.phone}
                onChange={(e) => updateAdopter('phone', e.target.value)}
                placeholder="Phone number"
                type="tel"
                hasError={errors.phone}
              />
            </PortalField>

            <PortalField label="Street Address *" error="Required" showError={errors.street} fullWidth>
              <PortalInput
                value={adopter.street}
                onChange={(e) => updateAdopter('street', e.target.value)}
                placeholder="Street address"
                hasError={errors.street}
              />
            </PortalField>

            <PortalField label="Apt / Suite">
              <PortalInput
                value={adopter.apt}
                onChange={(e) => updateAdopter('apt', e.target.value)}
                placeholder="Apt / Suite"
              />
            </PortalField>

            <PortalField label="City *" error="Required" showError={errors.city}>
              <PortalInput
                value={adopter.city}
                onChange={(e) => updateAdopter('city', e.target.value)}
                placeholder="City"
                hasError={errors.city}
              />
            </PortalField>

            <PortalField label="State *" error="Required" showError={errors.state}>
              <PortalSelect
                value={adopter.state}
                onChange={(e) => updateAdopter('state', e.target.value)}
                hasError={errors.state}
              >
                <option value="">Select state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </PortalField>

            <PortalField label="Zip Code *" error="5-digit zip required" showError={errors.zip}>
              <PortalInput
                value={adopter.zip}
                onChange={(e) => updateAdopter('zip', e.target.value)}
                placeholder="Zip code"
                hasError={errors.zip}
              />
            </PortalField>
          </FormGrid>

          <ModalActions>
            <Button variant="green" onClick={handleNext}>
              Continue
            </Button>
          </ModalActions>
        </div>
      )}

      {/* Step 2: Confirm */}
      {step === 2 && (
        <div>
          <ModalTitle>Confirm owner</ModalTitle>
          <p className={styles.modalDescription}>Review the details below before assigning the owner.</p>
          <ConfirmSummary
            sections={[
              {
                title: 'Pet',
                fields: [
                  { label: 'Chip ID', value: chip.id },
                  { label: 'Pet Name', value: chip.petName },
                  { label: 'Species', value: chip.species },
                  { label: 'Breed', value: chip.breed },
                ],
              },
              {
                title: 'New Owner',
                fields: [
                  { label: 'Name', value: `${adopter.firstName} ${adopter.lastName}` },
                  { label: 'Email', value: adopter.email },
                  { label: 'Phone', value: adopter.phone },
                  { label: 'Address', value: fullAddress },
                ],
              },
            ]}
          />
          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button variant="green" onClick={handleConfirm}>
              Add Owner
            </Button>
          </ModalActions>
        </div>
      )}
    </Modal>
  );
}
